import { css } from "emotion";
import { Component } from "preact";
import { connect } from "react-redux";
import { formValueSelector, reduxForm } from "redux-form";
import ActionBar from "../../components/ActionBar";
import ActionButton from "../../components/ActionButton";
import BookingHeader from "../../components/BookingHeader";
import Button from "../../components/Button";
import Content from "../../components/Content";
import CountDown from "../../components/CountDown";
import Page from "../../components/Page";
import TwoCols from "../../components/TwoCols";
import WarningMessage from "../../components/WarningMessage";
import {
  finishAssigning,
  getAssignedProfiles
} from "../../state/actions/attendeeProfileActions";
import { getEvent } from "../../state/actions/eventActions";
import { getOrder } from "../../state/actions/orderActions";
import { setSeatingMapRequired } from "../../state/actions/seatingMapActions";
import { updateTitle } from "../../utils/BrowserUtils";
import { scrollToErrors } from "../../utils/ScrollToErrors";
import findApplicableSeatingMapId from "../../utils/SeatingMapUtils";
import { getTicketValues } from "../../utils/stateConstructors";
import AssignSummary from "./AssignSummary";
import ExistingProfileModal from "./ExistingProfileModal";
import HybridSummary from "./HybridSummary";
import ProfileModal from "./ProfileModal";
import TicketList from "./TicketList";
import Alert from "../../components/Alert";
const assignedTicketSummaryStyle = css({
  display: "none",
  "@media (max-width:600px)": {
    display: "inherit",
    boxShadow: "0 1px 8px 0 rgba(9, 30, 66, 0.13)",
    padding: "1px 0px 0px 0px"
  }
});

const desktopTimerStyle = css({
  display: "inherit",
  "@media (max-width:600px)": {
    display: "none"
  }
});

const manualOrderStyle = css({
  ".columns.two-cols": {
    margin: 0
  }
});

const formName = "assign";

class AssignTickets extends Component {
  componentDidMount = () => {
    const {
      event,
      eventSlug,
      orderId,
      getEvent,
      attendeeProfile,
      getAssignedProfiles
    } = this.props;
    if (!event.data && !event.loading) {
      getEvent(eventSlug, undefined, false, orderId, true);
    }
    if (attendeeProfile.profiles.length === 0 && !attendeeProfile.loading) {
      getAssignedProfiles(orderId);
    }
    updateTitle("Assign Tickets", event);
  };

  save = () => {
    const { order, event, finishAssigning, seatingMap } = this.props;
    const seatingMapRequired = seatingMap.seatingMapRequired;
    finishAssigning(
      event.data.slug,
      event.data,
      order.data,
      seatingMapRequired
    );
  };

  componentDidUpdate = () => {
    const { event, order, seatingMap } = this.props;
    if (event.data && order.data && !seatingMap.seatingMapEvaluated) {
      this.determineSeatingMap(event.data, order.data);
    }
  };

  determineSeatingMap = (eventData, orderData) => {
    const { setSeatingMapRequired } = this.props;
    const seatingMapId = findApplicableSeatingMapId(eventData, orderData);
    setSeatingMapRequired(seatingMapId);
  };

  getForm = (ticketsGroupedByType, ticketsGroupedByAttendeeProfile) => {
    const {
      seatingMap,
      additionalQuestionsPreview,
      attendeeProfile
    } = this.props;

    const { profiles, loading, token } = attendeeProfile;
    const profileLookup = profiles.reduce((map, profile) => {
      map[profile._id] = profile;
      return map;
    }, {});

    let detailsProps = {
      saveDetails: this.save,
      seatingMapAvailable: seatingMap.seatingMapRequired,
      additionalQuestionsPreview,
      ticketsGroupedByType,
      profileLookup,
      profiles,
      ticketsGroupedByAttendeeProfile,
      loading,
      token
    };
    return <DetailsForm {...detailsProps} />;
  };

  render({ event, order, seatingMap, isGlobal, attendeeProfile }) {
    const { token } = attendeeProfile;
    const { attendeeModal } = attendeeProfile;
    if (order && order.orderGet.error) {
      return (
        <Page>
          <WarningMessage title="Order could not be loaded" />
        </Page>
      );
    }
    if ((event.loading || !event.data || !order.data) && !attendeeModal.show) {
      return;
    }
    if (order && order.data.status === "complete" && !token) {
      return (
        <Page>
          <WarningMessage title="Order has been completed" />
        </Page>
      );
    }
    const ticketsGroupedByType = Object.values(
      order.data.tickets.reduce((map, ticket) => {
        if (!map[ticket.ticketTypeId]) {
          map[ticket.ticketTypeId] = {
            tickets: [],
            ticketTypeName: ticket.ticketTypeName
          };
        }
        map[ticket.ticketTypeId].tickets.push(ticket);
        return map;
      }, {})
    );
    const ticketsGroupedByAttendeeProfile = order.data.tickets.reduce(
      (map, ticket) => {
        if (!ticket.attendeeProfileId) {
          map["unassigned"].push(ticket);
        } else {
          if (!map[ticket.attendeeProfileId]) {
            map[ticket.attendeeProfileId] = [];
          }
          map[ticket.attendeeProfileId].push(ticket);
        }
        return map;
      },
      { unassigned: [] }
    );

    const body = (
      <div className={token ? manualOrderStyle : false}>
        <div className={assignedTicketSummaryStyle}>
          {!token ? <CountDown margin="0px 0px 24px 0px" /> : null}
          <AssignSummary ticketsGroupedByType={ticketsGroupedByType} />
        </div>
        <TwoCols>
          <div>
            {!token ? (
              <div className={desktopTimerStyle}>
                {" "}
                <CountDown margin="0px 0px 24px 0px" />
              </div>
            ) : null}
            {this.getForm(
              ticketsGroupedByType,
              ticketsGroupedByAttendeeProfile
            )}
          </div>
          <HybridSummary
            token={token}
            ticketsGroupedByType={ticketsGroupedByType}
          />
        </TwoCols>
      </div>
    );
    if (token) {
      return body;
    }
    return (
      <div>
        <Page isGlobal={isGlobal}>
          <Content>
            <BookingHeader
              event={event}
              orderId={order.data._id}
              includeSeatingMap={seatingMap.seatingMapRequired}
              isGlobal={isGlobal}
            />
            {body}
          </Content>
        </Page>
      </div>
    );
  }
}

let DetailsForm = ({
  event,
  order,
  handleSubmit,
  saveDetails,
  seatingMapAvailable,
  loading,
  isGlobal,
  ticketsGroupedByType,
  profileLookup,
  profiles,
  ticketsGroupedByAttendeeProfile,
  token
}) => {
  const assignLaterText = `If you don’t yet know who your guests are then you can do this at a later date via your order confirmation email.`;

  let detailsAdditionQuestions =
    !isGlobal &&
    event.data.additionalQuestions &&
    event.data.additionalQuestions.length
      ? event.data.additionalQuestions.filter(q => !q.askAfterCheckout)
      : [];
  detailsAdditionQuestions = detailsAdditionQuestions.map(q => {
    if (q.type === "multiSelect" && q.options && q.options.length) {
      q.options = q.options.map(o => {
        const newOption = o.label
          ? {
              ...o
            }
          : {
              ...o,
              id: o._id,
              label: o.value,
              value: false
            };
        return newOption;
      });
    }
    return q;
  });
  const btnText = seatingMapAvailable ? "Select Seats" : "Save & Checkout";
  return (
    <div>
      {!token ? (
        <div style={{ marginBottom: 24 }}>
          <h2 class="sub-header">Please assign your tickets</h2>
          <Alert style={"info"}>
            <p style={{ margin: 0 }}>
              If you don’t know who your guests are, you can{" "}
              <AssignLaterLink saveDetails={saveDetails} />
            </p>
          </Alert>
        </div>
      ) : null}
      <h2 class="sub-header" style={{ fontWeight: 600 }}>
        Your Tickets
      </h2>
      <ProfileModal profileLookup={ticketsGroupedByAttendeeProfile} />
      <ExistingProfileModal />
      <form onSubmit={handleSubmit(saveDetails)}>
        {ticketsGroupedByType.map((ticketTypes, i) => {
          return (
            <TicketList
              profileLookup={profileLookup}
              profiles={profiles}
              {...ticketTypes}
              index={i}
              ticketsGroupedByAttendeeProfile={ticketsGroupedByAttendeeProfile}
              token={token}
            />
          );
        })}
        {!token ? (
          <ActionBar>
            <ActionButton
              disabled={order.timeExpired}
              loading={loading || order.loading}
              dataCY="checkout"
            >
              {btnText}
            </ActionButton>
          </ActionBar>
        ) : null}
      </form>
    </div>
  );
};

let AssignLaterLink = ({ handleSubmit, saveDetails }) => {
  return (
    <a
      style={{ cursor: "pointer" }}
      data-cy={"assign-later"}
      onClick={handleSubmit(saveDetails)}
    >
      Assign tickets later.
    </a>
  );
};

AssignLaterLink = reduxForm({
  form: formName
})(AssignLaterLink);

DetailsForm = reduxForm({
  form: formName,
  destroyOnUnmount: false,
  onSubmitFail: errors => {
    scrollToErrors();
  }
})(DetailsForm);

const selector = formValueSelector(formName);

DetailsForm = connect(
  (state, ownProps) => {
    const tickets = getTicketValues(
      state.order.data,
      state.event.data.additionalQuestions,
      selector(state, "tickets")
    );
    const initialValues = {
      tickets
    };
    return {
      initialValues: initialValues,
      event: state.event,
      order: state.order
    };
  },
  {}
)(DetailsForm);

export default connect(
  (state, ownProps) => {
    return {
      event: state.event,
      order: state.order,
      attendeeProfile: state.attendeeProfile,
      seatingMap: state.seatingMap
    };
  },
  {
    getEvent,
    getOrder,
    setSeatingMapRequired,
    getAssignedProfiles,
    finishAssigning
  }
)(AssignTickets);



// WEBPACK FOOTER //
// ./routes/conferences/AssignTickets.js